import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import {
  fetchLocationCircuits,
  createLocationCircuit,
  updateLocationCircuit,
  deleteLocationCircuit,
  assignChargerToLocationCircuit,
  unassignChargerFromLocationCircuit,
  fetchChargers,
  enableTenantDlm,
  getDlmAddonRequired,
  getDisplayErrorMessage,
  formatCents,
  type BuildingCircuitRow,
  type CircuitUpsertBody,
  type BreakerTripClass,
  type Charger,
  type DlmAddonRequired,
} from '@/lib/api';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import formStyles from '@/components/ui/Form.module.css';
import { Switch } from '@/components/ui/Switch';
import { dlmAddonPriceLine } from '@/lib/planPrices';

/**
 * Building (location) Circuits surface — DLM admin UI, unit 4.
 *
 * The multifamily sibling of the fleet Circuits tab in FleetDetailPage.tsx.
 * Same look and shape: CRUD on a building's circuits plus assign/unassign of
 * member chargers, with read-only observability of what the control loop
 * last pushed. It runs against the unit-1 building-circuits CRUD at
 * /admin/locations/:id/circuits.
 *
 * Forked from the fleet Circuits tab rather than extracting a shared
 * component, so the fleet tab stays byte-unchanged (the same way the SP
 * dashboard forked LocationInfoCard into SalesPartnerLocationInfoCard). The
 * charger roster comes from the general /chargers list scoped to this
 * location, which carries circuit_id and the loop's last cap on the full
 * doc; the fleet tab uses its dedicated fleet-chargers projection instead.
 *
 * RBAC is enforced by the route (requireTenantScope): a tenant admin manages
 * their own building's circuits, a super_admin manages any. This surface
 * only renders inside a location detail page the caller is already scoped to.
 */

const TRIP_CLASS_OPTIONS: BreakerTripClass[] = ['B', 'C', 'D'];

/** Recommended DLM ceiling — mirrors the backend's 0.8 * breaker_rating_a
 *  derate WARNING (guidance, never a hard cap). */
function derateCeiling(breakerRatingA: number): number {
  return Math.floor(0.8 * breakerRatingA);
}

function fmtCap(a: number | null | undefined): string {
  return a === null || a === undefined ? '—' : `${a} A`;
}

/** Compact "time since" for the loop's last push. */
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--color-tx-m)' }}>{label}</span>
      {children}
    </label>
  );
}

function chargerLabel(c: Charger): string {
  return c.display_name ? `${c.display_name} (${c.identity})` : c.identity;
}

export function BuildingCircuitsSection({ locationId }: { locationId: string }) {
  const qc = useQueryClient();
  const circuitsQ = useQuery({
    queryKey: ['location-circuits', locationId],
    queryFn: () => fetchLocationCircuits(locationId),
  });
  // The building's chargers carry circuit_id + the loop's last-pushed cap on
  // the full /chargers doc — powering membership, the assign picker, and the
  // read-only observability columns.
  const chargersQ = useQuery({
    queryKey: ['location-chargers', locationId],
    queryFn: () => fetchChargers({ location_id: locationId, limit: 200 }).then((r) => r.data),
  });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<BuildingCircuitRow | null>(null);
  const [managing, setManaging] = useState<BuildingCircuitRow | null>(null);

  const remove = useMutation({
    mutationFn: (id: string) => deleteLocationCircuit(locationId, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['location-circuits', locationId] });
      void qc.invalidateQueries({ queryKey: ['location-chargers', locationId] });
    },
  });

  const chargers = chargersQ.data ?? [];
  const capSum = (circuitId: string): number =>
    chargers
      .filter((c) => c.circuit_id === circuitId)
      .reduce((acc, c) => acc + (c.dlm_last_cap_a ?? 0), 0);

  const columns: Column<BuildingCircuitRow>[] = [
    { key: 'name', header: 'Circuit', cell: (r) => r.name },
    { key: 'budget', header: 'DLM budget', cell: (r) => <code>{r.limit_a} A</code> },
    {
      key: 'breaker',
      header: 'Breaker',
      cell: (r) => <code>{r.breaker_rating_a} A · {r.breaker_trip_class}</code>,
    },
    {
      key: 'load',
      header: 'Loop load',
      cell: (r) => {
        const sum = capSum(r._id);
        return (
          <code style={{ color: sum > r.limit_a ? 'var(--evi-error, #ef4444)' : 'var(--color-tx)' }}>
            {sum} / {r.limit_a} A
          </code>
        );
      },
    },
    { key: 'chargers', header: 'Chargers', cell: (r) => String(r.charger_count) },
    {
      key: 'is_active',
      header: 'Loop',
      cell: (r) => (
        <span style={{
          padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 600,
          background: r.is_active
            ? 'color-mix(in srgb, var(--evi-success, #10b981) 14%, transparent)'
            : 'color-mix(in srgb, var(--color-tx-m) 18%, transparent)',
          color: r.is_active ? 'var(--evi-success, #10b981)' : 'var(--color-tx-m)',
        }}>
          {r.is_active ? 'Managed' : 'Paused'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button type="button" aria-label="Manage chargers" onClick={() => setManaging(r)}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-accent, #2ECC71)', fontSize: 12, fontWeight: 600 }}>
            Chargers
          </button>
          <button type="button" aria-label="Edit circuit" onClick={() => { setEditing(r); setFormOpen(true); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-tx-m)' }}>
            <Pencil size={14} />
          </button>
          <button type="button" aria-label="Delete circuit"
            onClick={() => { if (confirm(`Delete circuit "${r.name}"? Member chargers will be unassigned.`)) remove.mutate(r._id); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-error, #ef4444)' }}>
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Circuits ({circuitsQ.data?.length ?? 0})</h2>
        <button type="button" onClick={() => { setEditing(null); setFormOpen(true); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', background: 'var(--evi-accent, #2ECC71)',
            color: '#000', border: 'none', borderRadius: 8, fontSize: 12,
            fontWeight: 600, cursor: 'pointer',
          }}>
          <Plus size={12} /> Add circuit
        </button>
      </div>
      <p style={{ color: 'var(--color-tx-m)', fontSize: 12, margin: '0 0 14px' }}>
        The control loop balances each <strong>Managed</strong> circuit's chargers against its amp budget.
        “Loop load” sums the caps the loop last pushed to member chargers. DLM bills per managed charger
        when the building has DLM enabled.
      </p>

      {circuitsQ.isLoading && <p>Loading…</p>}
      {(circuitsQ.data ?? []).length === 0 && !circuitsQ.isLoading && (
        <p style={{ color: 'var(--color-tx-m)' }}>No circuits at this building yet.</p>
      )}
      {(circuitsQ.data ?? []).length > 0 && (
        <ResponsiveTable<BuildingCircuitRow>
          rows={circuitsQ.data ?? []}
          columns={columns}
          rowKey={(r) => r._id}
        />
      )}

      {formOpen && (
        <BuildingCircuitFormModal
          locationId={locationId}
          editing={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ['location-circuits', locationId] });
            setFormOpen(false);
            setEditing(null);
          }}
        />
      )}

      {managing && (
        <BuildingCircuitChargersModal
          locationId={locationId}
          circuit={managing}
          chargers={chargers}
          onClose={() => setManaging(null)}
          onChanged={() => {
            void qc.invalidateQueries({ queryKey: ['location-chargers', locationId] });
            void qc.invalidateQueries({ queryKey: ['location-circuits', locationId] });
          }}
        />
      )}
    </div>
  );
}

function BuildingCircuitFormModal({
  locationId, editing, onClose, onDone,
}: {
  locationId: string;
  editing: BuildingCircuitRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [limit, setLimit] = useState(editing ? String(editing.limit_a) : '');
  const [breaker, setBreaker] = useState(editing ? String(editing.breaker_rating_a) : '');
  const [tripClass, setTripClass] = useState<BreakerTripClass>(editing?.breaker_trip_class ?? 'C');
  const [isActive, setIsActive] = useState(editing?.is_active ?? true);
  const [err, setErr] = useState<string | null>(null);
  // 402 DLM_ADDON_REQUIRED from create (the DLM paywall): rendered as an
  // inline prompt — price, today's managed-charger count, "Add the DLM
  // add-on" — instead of the red error, because the fix is one click away.
  // lastBody is the create the 402 interrupted, retried once the add-on is
  // on. Edits never trip the gate (it is on create only).
  const [addon, setAddon] = useState<DlmAddonRequired | null>(null);
  const [lastBody, setLastBody] = useState<CircuitUpsertBody | null>(null);

  const limitNum = Number(limit);
  const breakerNum = Number(breaker);
  // Live, NON-blocking derate guidance (mirrors the backend 0.8x warning).
  const derateWarn =
    breakerNum > 0 && limitNum > 0.8 * breakerNum
      ? `Budget (${limitNum} A) exceeds 80% of the breaker (${breakerNum} A). Recommended ceiling ${derateCeiling(breakerNum)} A — you can save anyway.`
      : null;

  const mut = useMutation({
    mutationFn: (body: CircuitUpsertBody) =>
      editing ? updateLocationCircuit(locationId, editing._id, body) : createLocationCircuit(locationId, body),
    onSuccess: () => onDone(),
    onError: async (e: Error) => {
      const required = await getDlmAddonRequired(e);
      if (required) { setAddon(required); return; }
      setErr(e.message || 'Failed to save circuit');
    },
  });

  // "Add the DLM add-on": enable on the building's tenant (the 402 carries
  // tenant_id because this section only knows the location), then retry the
  // create. A caller who reached the create route is tenant_admin or
  // super_admin, so the ROLE side of the enable route accepts them; the
  // server's can_enable also folds in whether the tenant has a live
  // subscription (the enable route 402s SUBSCRIPTION_REQUIRED without one,
  // because DLM bills as an item on the subscription). When can_enable is
  // false the panel shows the server's hint and a link to Settings → Billing
  // instead of a button that would bounce off the same wall.
  const enableM = useMutation({
    mutationFn: (tenantId: string) => enableTenantDlm(tenantId),
    onSuccess: () => {
      setAddon(null);
      if (lastBody) mut.mutate(lastBody);
    },
    onError: async (e: Error) => {
      setErr(await getDisplayErrorMessage(e, 'Could not add the DLM add-on. Try again from Settings → Billing.'));
    },
  });

  const submit = () => {
    setErr(null);
    setAddon(null);
    if (!name.trim()) { setErr('Name is required.'); return; }
    if (!(breakerNum > 0)) {
      setErr('Breaker rating must be a positive number.');
      return;
    }
    // Hard 6A floor — distinct from the soft derate WARNING above.
    if (!(limitNum >= 6)) {
      setErr('DLM budget must be at least 6A, the lowest current an EV will charge at.');
      return;
    }
    const body: CircuitUpsertBody = {
      name: name.trim(),
      limit_a: limitNum,
      breaker_rating_a: breakerNum,
      breaker_trip_class: tripClass,
      is_active: isActive,
    };
    setLastBody(body);
    mut.mutate(body);
  };

  // The building's cadence's load management price from its own Stripe
  // Price, exact cents, or "EV Initiative confirms the price" (Warren, 21
  // October 2026, night).
  const perCharger = addon ? dlmAddonPriceLine(addon, formatCents) : '';
  // The tenant to enable on, only when the server says this caller may.
  const enableTenantId = addon?.can_enable ? (addon.tenant_id ?? null) : null;

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(480px, 94vw)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--color-sf, #1a1a1a)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>{editing ? 'Edit circuit' : 'Add circuit'}</h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'var(--admin-pair-columns, 1fr 1fr)', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Name"><input className={formStyles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Garage feed A" /></Field>
          </div>
          <Field label="DLM budget (A)"><input className={formStyles.input} type="number" min={6} value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="80" /></Field>
          <Field label="Breaker rating (A)"><input className={formStyles.input} type="number" value={breaker} onChange={(e) => setBreaker(e.target.value)} placeholder="100" /></Field>
          <Field label="Breaker trip class">
            <select className={formStyles.select} value={tripClass} onChange={(e) => setTripClass(e.target.value as BreakerTripClass)}>
              {TRIP_CLASS_OPTIONS.map((c) => <option key={c} value={c}>Class {c}</option>)}
            </select>
          </Field>
          <Field label="Loop management">
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} label="Active: loop manages it" />
          </Field>
        </div>

        <p style={{ margin: '12px 0 0', fontSize: 11, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--color-tx)' }}>Trip class</strong> sets how much momentary overcurrent the
          breaker tolerates before it trips. Most EV circuits are B or C. Match the letter printed on your
          physical breaker, e.g. C32.
        </p>

        {derateWarn && (
          <div style={{ marginTop: 12, padding: '8px 10px', color: 'var(--evi-warning, #f59e0b)', background: 'color-mix(in srgb, var(--evi-warning, #f59e0b) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-warning, #f59e0b) 30%, transparent)', borderRadius: 6, fontSize: 12, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{derateWarn}</span>
          </div>
        )}
        {addon && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'color-mix(in srgb, var(--evi-accent, #2ECC71) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-accent, #2ECC71) 35%, transparent)', borderRadius: 8, fontSize: 12, lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-tx)', marginBottom: 4 }}>Dynamic load management add-on</div>
            {enableTenantId ? (
              <>
                <p style={{ margin: 0, color: 'var(--color-tx-m)' }}>
                  Circuits need the DLM add-on ({perCharger}). Only chargers assigned
                  to a circuit count — this building's operator has {addon.managed_charger_count} managed
                  charger{addon.managed_charger_count === 1 ? '' : 's'} today.
                </p>
                <button type="button" disabled={enableM.isPending || mut.isPending}
                  onClick={() => { setErr(null); enableM.mutate(enableTenantId); }}
                  style={{ marginTop: 10, padding: '7px 12px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {enableM.isPending ? 'Adding…' : 'Add the DLM add-on'}
                </button>
              </>
            ) : (
              // No enable button: the server said this caller cannot add the
              // add-on from here (no live subscription). The hint is the
              // server's plain-English next step; the link is a full page
              // load to Settings → Billing, where the subscription starts.
              <>
                <p style={{ margin: 0, color: 'var(--color-tx-m)' }}>
                  Circuits need the DLM add-on ({perCharger}), billed on your
                  subscription. {addon.hint}
                </p>
                {addon.subscribe_url && (
                  <a href={addon.subscribe_url}
                    style={{ display: 'inline-block', marginTop: 10, padding: '7px 12px', background: 'var(--evi-accent, #2ECC71)', color: '#000', borderRadius: 8, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                    Open Settings → Billing
                  </a>
                )}
              </>
            )}
          </div>
        )}
        {err && (
          <div style={{ marginTop: 12, padding: '8px 10px', color: 'var(--evi-error, #ef4444)', background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)', borderRadius: 6, fontSize: 12 }}>{err}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={mut.isPending || enableM.isPending}
            style={{ padding: '8px 14px', background: 'transparent', color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          {/* Submit is parked while the add-on panel is up: a re-click would
              hit the same 402. The panel's own action (enable-then-retry, or
              the Billing link) is the only way forward. */}
          <button type="button" onClick={submit} disabled={mut.isPending || enableM.isPending || addon !== null}
            style={{ padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: addon !== null ? 'not-allowed' : 'pointer', opacity: addon !== null ? 0.5 : 1 }}>
            {mut.isPending ? 'Saving…' : (editing ? 'Save changes' : 'Add circuit')}
          </button>
        </div>
      </div>
    </div>
  );
}

function BuildingCircuitChargersModal({
  locationId, circuit, chargers, onClose, onChanged,
}: {
  locationId: string;
  circuit: BuildingCircuitRow;
  chargers: Charger[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [pick, setPick] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const members = chargers.filter((c) => c.circuit_id === circuit._id);
  const assignable = chargers.filter((c) => c.circuit_id !== circuit._id);
  const capSum = members.reduce((acc, c) => acc + (c.dlm_last_cap_a ?? 0), 0);

  const assign = useMutation({
    mutationFn: (chargerId: string) => assignChargerToLocationCircuit(locationId, circuit._id, chargerId),
    onSuccess: () => { setPick(''); setErr(null); onChanged(); },
    onError: (e: Error) => setErr(e.message || 'Failed to assign charger'),
  });
  const unassign = useMutation({
    mutationFn: (chargerId: string) => unassignChargerFromLocationCircuit(locationId, circuit._id, chargerId),
    onSuccess: () => onChanged(),
    onError: (e: Error) => setErr(e.message || 'Failed to remove charger'),
  });

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 94vw)', maxHeight: '90vh', overflowY: 'auto', background: 'var(--color-sf, #1a1a1a)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 12, padding: 20 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 16 }}>{circuit.name} — chargers</h2>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--color-tx-m)' }}>
          Loop load{' '}
          <code style={{ color: capSum > circuit.limit_a ? 'var(--evi-error, #ef4444)' : 'var(--color-tx)' }}>{capSum} / {circuit.limit_a} A</code>
          {circuit.is_active ? '' : ' · loop paused (is_active off)'}
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <select className={formStyles.select} value={pick} onChange={(e) => setPick(e.target.value)} style={{ flex: 1 }}>
            <option value="">— Select a charger to assign —</option>
            {assignable.map((c) => (
              <option key={c._id} value={c._id}>{chargerLabel(c)}{c.circuit_id ? ' · move' : ''}</option>
            ))}
          </select>
          <button type="button" disabled={!pick || assign.isPending}
            onClick={() => { setErr(null); if (pick) assign.mutate(pick); }}
            style={{ padding: '8px 14px', background: 'var(--evi-accent, #2ECC71)', color: '#000', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: pick ? 'pointer' : 'default', opacity: pick ? 1 : 0.5 }}>
            Assign
          </button>
        </div>

        {err && (
          <div style={{ marginBottom: 12, padding: '8px 10px', color: 'var(--evi-error, #ef4444)', background: 'color-mix(in srgb, var(--evi-error, #ef4444) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 30%, transparent)', borderRadius: 6, fontSize: 12 }}>{err}</div>
        )}

        {members.length === 0 ? (
          <p style={{ color: 'var(--color-tx-m)', fontSize: 13 }}>No chargers on this circuit yet.</p>
        ) : (
          <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 72px 90px 44px', minWidth: 410, gap: 10, fontSize: 11, color: 'var(--color-tx-m)', padding: '0 4px 4px' }}>
              <span>Charger</span><span>Last cap</span><span>Pushed</span><span />
            </div>
            {members.map((c) => (
              <div key={c._id} style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 1fr) 72px 90px 44px', minWidth: 410, gap: 10, alignItems: 'center', padding: '8px 4px', borderTop: '1px solid var(--color-brd, var(--evi-border))', fontSize: 13 }}>
                <span>{chargerLabel(c)}</span>
                <code>{fmtCap(c.dlm_last_cap_a)}</code>
                <code style={{ color: 'var(--color-tx-m)' }}>{timeAgo(c.dlm_last_pushed_at)}</code>
                <button type="button" aria-label="Remove from circuit" disabled={unassign.isPending}
                  onClick={() => unassign.mutate(c._id)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--evi-error, #ef4444)' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose}
            style={{ padding: '8px 14px', background: 'transparent', color: 'var(--color-tx)', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
}
